import React, { createContext, useContext, useState, useEffect } from "react";

const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧", dir: "ltr" },
  { code: "fa", label: "فارسی", flag: "🇮🇷", dir: "rtl" },
  { code: "zh", label: "中文", flag: "🇨🇳", dir: "ltr" },
  { code: "ru", label: "Русский", flag: "🇷🇺", dir: "ltr" },
  { code: "es", label: "Español", flag: "🇪🇸", dir: "ltr" },
  { code: "pt", label: "Português", flag: "🇵🇹", dir: "ltr" },
];

const T = {
  en: {
    sidebarDashboard: "Dashboard",
    sidebarWorkers: "Workers",
    sidebarEarnings: "Earnings",
    sidebarConnect: "Connect Miners",
    sidebarNotifications: "Notifications",
    sidebarSettings: "Settings",
    sidebarAdmin: "Admin",
    adminTitle: "Admin",
    adminSub: "Pool overview, payouts and content. Every action here is re-checked server-side.",
    adminUsers: "Users",
    adminVerified: "verified",
    adminReferred: "referred",
    adminWorkers: "Workers online",
    adminOf: "of",
    adminPayoutsPending: "Payouts pending",
    adminReferralPaid: "Referral rewards paid",
    adminCredits: "credits",
    adminPayouts: "Payout requests",
    adminNoPayouts: "No payout requests yet.",
    adminMarkPaid: "Mark paid",
    adminMarkFailed: "Mark failed",
    adminConfirmStatus: "Change payout status",
    adminTxidPrompt: "Transaction ID (optional):",
    adminArticles: "Articles",
    adminPublished: "published",
    adminDrafts: "draft",
    adminArticle: "Article",
    adminWords: "Words",
    adminArticlesNote: "Articles are markdown in content/blog/. Publishing is a file edit plus a deploy by design — git history is the audit trail, and an article publishable from a web form is one publishable without reading its fact-check list.",
    adminRecentUsers: "Recent users",
    adminReferredBy: "Referred by",
    adminForbiddenHint: "This page requires an admin account. Add your user ID to ADMIN_USER_IDS in wrangler.toml.",
    blogTitle: "Blog",
    blogSub: "Mining, pools and Bitcoin — written for people who actually run hardware.",
    blogEmpty: "No articles published yet.",
    blogMinRead: "min read",
    blogAll: "All articles",
    blogAlsoIn: "Also available in:",
    blogCtaTitle: "Start mining with Hashrial",
    blogCtaBody: "2% flat fee, 98% to you, and a proxy built for networks where the big pools are hard to reach.",
    blogCtaAction: "Create an account →",
    navBlog: "Blog",
    referralTitle: "Refer & earn",
    referralSub: "Earn 1% of what the people you bring in mine — paid from our fee, not from theirs.",
    referralYourLink: "Your referral link",
    referralCopy: "Copy link",
    referralCopied: "Copied",
    referralCodeIs: "Your code:",
    referralChooseCode: "Choose your own code",
    referralCodePlaceholder: "e.g. SINA",
    referralClaimCode: "Claim",
    referralSaving: "Saving…",
    referralCodeHint: "4–16 letters and numbers. Pick something easy to say out loud.",
    referralCodeKeepsWorking: "Links you have already shared keep working — every code you claim stays yours.",
    referralCodeSaved: "Code updated",
    referralCodeTaken: "That code is already taken",
    referralCodeReserved: "That code is reserved",
    referralCodeInvalid: "Use 4–16 letters and numbers only",
    referralCodeFailed: "Could not save. Please try again.",
    referralEarned: "Referral earnings",
    referralLast30: "Last 30 days",
    referralRate: "You earn",
    referralPeople: "People you brought in",
    referralActiveNote: "Active means the account has verified its email and can mine. Unverified signups are counted separately.",
    referralHowTitle: "How it works",
    referralHowBody: "Hashrial keeps a 2% fee on what a miner earns. When someone signs up through your link, you receive half of that fee — 1% of everything they mine, for as long as they mine. It comes out of our share, so the person you invited still keeps 98% exactly as they would have. Rewards are credited daily and appear in your balance.",
    referralYourPeople: "Your referrals",
    referralNobodyYet: "Nobody has signed up through your link yet. Share it and your rewards will appear here.",
    referralUser: "User",
    referralJoined: "Joined",
    referralActive: "Active",
    referralPendingVerify: "Unverified",
    sidebarReferral: "Refer & earn",
    headerWelcome: "Welcome back",
    headerLive: "Live",
    signOut: "Sign out",
    darkMode: "Dark Mode",
    lightMode: "Light Mode",
    language: "Language",
    loading: "Loading…",
    loading2: "Loading dashboard…",
    loadingWorkers: "Loading workers…",
    noData: "No data",
    noDataWorker: "No data yet for this worker.",
    errorPrefix: "⚠",

    dashboardTitle: "Dashboard",
    dashboardSub: "Live pool stats · refreshes every 60s",
    hashrate10m: "Hashrate (10m)",
    hashrate5m: "Hashrate (5m)",
    activeWorkers: "Active Workers",
    balance: "Balance",
    last24h: "Last 24h",
    totalEarned: "Total Earned",
    acceptance: "Acceptance",
    hashrateHistory: "Hashrate History",
    noHashrateData: "No hashrate data yet. Connect your miners to start.",
    viewAll: "View all →",

    workersTitle: "Workers",
    workersSub: "All miners connected to your account",
    total: "Total",
    online: "online",
    offline: "offline",
    all: "all",
    noWorkers: "No workers yet.",
    connectFirst: "Connect your first miner →",
    colWorker: "Worker",
    colStatus: "Status",
    col10m: "10m",
    col5m: "5m",
    col1h: "1h",
    col24h: "24h",
    colAccepted: "Accepted",
    colStale: "Stale",
    colLastSeen: "Last Seen",

    workerDetail: "Worker Detail",
    backToWorkers: "← Back to Workers",
    workerNotFound: "Worker not found.",
    hashrateChart: "Hashrate (24h)",
    shares: "Shares",
    accepted: "Accepted",
    stale: "Stale",

    earningsTitle: "Earnings",
    earningsSub: "Historical earnings and payment records",
    date: "Date",
    amount: "Amount",
    pending: "Pending",
    processing: "Processing",
    completed: "Completed",
    failed: "Failed",
    address: "Address",
    txid: "Transaction",
    records: "records",
    balance2: "Balance",
    earn24h: "24h Earnings",
    earnTotal: "Total Earned",
    paidOut: "Paid Out",
    settleDate: "Settle Date",
    loadingEarnings: "Loading earnings…",
    noEarnings: "No earnings data yet.",
    requestPayout: "Request Payout",
    payoutHistory: "Payout History",
    earningsHistory: "Earnings History",
    submitting: "Submitting",
    payoutMinNote: "Make sure you have set a payout address in Settings.",
    accountInfo: "Account",
    memberSince: "Member Since",
    settingsUserId: "User ID",
    page: "Page",
    of: "of",
    prev: "← Prev",
    next: "Next →",

    connectTitle: "Connect Your Miners",
    connectIntro: "Configure your ASIC or mining software with these settings to start earning.",
    stratumHost: "Stratum Host",
    stratumPort: "Port",
    workerLabel: "Worker Name",
    workerExample: "Replace WORKER_NAME with any label (e.g. rig01, asic1)",
    poolFee: "2% pool fee applies.",
    connectSteps: "Setup Instructions",
    step1: "Log into your miner admin panel",
    step2: "Set URL to",
    step3: "Set Worker to",
    step4: "Save and restart the miner",
    note: "Note",
    noteText: "Your miner will appear in the Workers page within 60 seconds of connecting.",
    whatTitle: "What you can expect",
    what1: "Your hashrate appears live on the Dashboard",
    what2: "Each worker is tracked separately",
    what3: "Earnings update every 60 seconds",
    what4: "Payouts available above 0.001 BTC",

    settingsTitle: "Settings",
    settingsSub: "Manage your account preferences",
    bitcoinAddress: "Bitcoin Address",
    bitcoinAddressLabel: "Your BTC payout address",
    bitcoinAddressHint: "Enter your Bitcoin wallet address for payouts.",
    save: "Save",
    saved: "Saved!",
    notifications: "Notifications",
    notifyOffline: "Worker offline alerts",
    notifyOfflineDesc: "Get notified when a worker goes offline",
    notifyHashrate: "Hashrate drop alerts",
    notifyHashrateDesc: "Get notified when hashrate drops significantly",
    notifyThreshold: "Drop threshold (%)",
    notifyThresholdDesc: "Minimum percentage drop to trigger alert",
    saving: "Saving…",

    notifTitle: "Notifications",
    notifSub: "Alerts and updates about your mining activity",
    noNotifs: "No notifications yet.",
    markAllRead: "Mark all as read",
    unread: "unread",
    notifWorkerOffline: "Worker offline",
    notifWorkerOnline: "Worker online",
    notifHashrateDrop: "Hashrate drop",
    justNow: "just now",
    minsAgo: "m ago",
    hoursAgo: "h ago",
    daysAgo: "d ago",

    loginTitle: "Sign in to your account",
    loginEmail: "Email",
    loginPassword: "Password",
    loginBtn: "Sign in →",
    loginNoAccount: "Don't have an account?",
    loginCreateOne: "Create one →",
    loginFailed: "Login failed",
    signingIn: "Signing in…",

    registerTitle: "Create your account",
    registerSub: "Your username will be your pool identity. Choose carefully — it cannot be changed.",
    registerUsername: "Username",
    registerUsernameHint: "3–20 chars, lowercase letters/numbers/underscore",
    registerEmail: "Email",
    registerPassword: "Password",
    registerPasswordHint: "10+ chars: upper, lower, number & symbol",
    hashing: "Hashing",
    updated: "Updated",
    ago: "ago",
    average: "average",
    sharesAccepted: "Shares accepted",
    workerStatus: "Worker status",
    live: "Live",
    faults: "Faults",
    none: "None",
    status: "Status",
    registerCheckEmailTitle: "Check your email",
    registerCheckEmailSub: "We've sent a verification link to your email address. Click it to confirm your account — you can start mining in the meantime.",
    registerContinueToDashboard: "Continue to dashboard \u2192",
    registerResendLink: "Didn't get it? Resend the email",
    registerResending: "Sending…",
    registerResendSent: "Sent. It should arrive within a minute.",
    registerResendWait: "You can resend shortly",
    registerResendFailed: "Couldn't resend. Try again in a moment.",
    registerCheckSpam: "If it is not in your inbox, check your spam folder.",
    registerEmailUnavailableTitle: "Account created",
    registerEmailUnavailableSub: "We could not send a verification email right now. Your account works — contact support to get it verified.",
    btcPrice: "Bitcoin price",
    backToHome: "Back to hashrial.com",
    tenMinAverage: "10-minute average",
    fiveMinAverage: "5-minute average",
    chartCollecting: "Collecting data — the chart fills in as samples arrive.",
    degraded: "Degraded",
    degradedHint: "Degraded = below 60% of a rig's own 1-hour average.",
    perThDay: "Per TH/day",
    showIn: "Show in",
    attentionCount: "{n} rigs need attention",
    allRigsHealthy: "All {n} rigs healthy",
    poolFeeNote: "Pool fee 2% — 1 share in every 50.",
    payoutProgress: "Toward minimum payout",
    payoutReady: "Ready to withdraw",
    priceUnavailable: "Price unavailable",
    priceDelayed: "Delayed",
    priceDelayedHint: "Live price feed is unreachable — showing the last known value.",
    currency: "Currency",
    exchangeAtSarraf: "Exchange at صراف →",
    rateFromSarraf: "صراف rate",
    rateFromSarrafHint: "Rate set from صراف rather than the public feed.",
    registerBtn: "Create account →",
    registerHasAccount: "Already have an account?",
    registerSignIn: "Sign in →",
    registerFailed: "Registration failed",
    creatingAccount: "Creating account…",
    registerUsernameError: "Username: 3-20 chars, lowercase/numbers/underscore",
    registerPasswordError: "Password must be at least 10 characters",
    registerTaken: "Username or email already taken",
    loginForgotPassword: "Forgot password?",
    forgotTitle: "Reset your password",
    forgotSub: "Enter your email and we'll send you a link to reset your password.",
    forgotEmail: "Email",
    forgotBtn: "Send reset link →",
    forgotSending: "Sending…",
    forgotSent: "If an account exists for that email, a reset link is on its way. Check your inbox.",
    forgotBackToLogin: "Back to sign in",
    resetTitle: "Choose a new password",
    resetSub: "Enter a new password for your account.",
    resetPassword: "New password",
    resetConfirm: "Confirm password",
    resetBtn: "Reset password →",
    resetSubmitting: "Resetting…",
    resetSuccess: "Password reset. You can now sign in with your new password.",
    resetMismatch: "Passwords don't match",
    resetInvalidLink: "This reset link is invalid or has expired.",
    resetGoToLogin: "Go to sign in →",
    verifyTitle: "Verifying your email…",
    verifySuccess: "Your email is verified!",
    verifySuccessSub: "You're all set.",
    verifyFailed: "This verification link is invalid or has expired.",
    verifyGoToDashboard: "Go to dashboard →",
    verifyGoToLogin: "Go to sign in →",
  },
  fa: {
    sidebarDashboard: "داشبورد",
    sidebarWorkers: "کارگران",
    sidebarEarnings: "درآمد",
    sidebarConnect: "اتصال ماینرها",
    sidebarNotifications: "اعلان‌ها",
    sidebarSettings: "تنظیمات",
    sidebarAdmin: "مدیریت",
    adminTitle: "مدیریت",
    adminSub: "نمای کلی استخر، برداشت‌ها و محتوا. همهٔ عملیات اینجا در سمت سرور دوباره بررسی می‌شود.",
    adminUsers: "کاربران",
    adminVerified: "تأییدشده",
    adminReferred: "معرفی‌شده",
    adminWorkers: "ماینرهای آنلاین",
    adminOf: "از",
    adminPayoutsPending: "برداشت‌های در انتظار",
    adminReferralPaid: "پاداش دعوت پرداخت‌شده",
    adminCredits: "رکورد",
    adminPayouts: "درخواست‌های برداشت",
    adminNoPayouts: "هنوز درخواست برداشتی ثبت نشده است.",
    adminMarkPaid: "ثبت پرداخت",
    adminMarkFailed: "ثبت ناموفق",
    adminConfirmStatus: "تغییر وضعیت برداشت",
    adminTxidPrompt: "شناسهٔ تراکنش (اختیاری):",
    adminArticles: "مقاله‌ها",
    adminPublished: "منتشرشده",
    adminDrafts: "پیش‌نویس",
    adminArticle: "مقاله",
    adminWords: "کلمه",
    adminArticlesNote: "مقاله‌ها به‌صورت markdown در content/blog/ نگهداری می‌شوند. انتشار عمداً نیازمند ویرایش فایل و استقرار است — تاریخچهٔ گیت سند ممیزی است، و مقاله‌ای که از یک فرم وب منتشر شود، مقاله‌ای است که بدون خواندن فهرست راستی‌آزمایی منتشر می‌شود.",
    adminRecentUsers: "کاربران اخیر",
    adminReferredBy: "معرف",
    adminForbiddenHint: "این صفحه به حساب مدیر نیاز دارد. شناسهٔ کاربری خود را به ADMIN_USER_IDS در wrangler.toml اضافه کنید.",
    blogTitle: "وبلاگ",
    blogSub: "ماینینگ، استخرها و بیت‌کوین — نوشته‌شده برای کسانی که واقعاً دستگاه دارند.",
    blogEmpty: "هنوز مقاله‌ای منتشر نشده است.",
    blogMinRead: "دقیقه مطالعه",
    blogAll: "همهٔ مقاله‌ها",
    blogAlsoIn: "به زبان‌های دیگر:",
    blogCtaTitle: "با هشریال استخراج کنید",
    blogCtaBody: "کارمزد ثابت ۲٪، ۹۸٪ سهم شما، و پروکسی‌ای که برای شبکه‌هایی ساخته شده که دسترسی به استخرهای بزرگ در آن‌ها دشوار است.",
    blogCtaAction: "ساخت حساب ←",
    navBlog: "وبلاگ",
    referralTitle: "دعوت و درآمد",
    referralSub: "یک درصد از آنچه افرادی که معرفی می‌کنید استخراج می‌کنند، از کارمزد ما پرداخت می‌شود، نه از سهم آن‌ها.",
    referralYourLink: "لینک دعوت شما",
    referralCopy: "کپی لینک",
    referralCopied: "کپی شد",
    referralCodeIs: "کد شما:",
    referralChooseCode: "کد دلخواه خود را انتخاب کنید",
    referralCodePlaceholder: "مثلاً SINA",
    referralClaimCode: "ثبت کد",
    referralSaving: "در حال ذخیره…",
    referralCodeHint: "۴ تا ۱۶ حرف و عدد. چیزی انتخاب کنید که گفتنش آسان باشد.",
    referralCodeKeepsWorking: "لینک‌هایی که قبلاً به اشتراک گذاشته‌اید همچنان کار می‌کنند — هر کدی که ثبت کنید برای همیشه مال شماست.",
    referralCodeSaved: "کد به‌روزرسانی شد",
    referralCodeTaken: "این کد قبلاً گرفته شده است",
    referralCodeReserved: "این کد رزرو شده است",
    referralCodeInvalid: "فقط ۴ تا ۱۶ حرف و عدد مجاز است",
    referralCodeFailed: "ذخیره نشد. دوباره تلاش کنید.",
    referralEarned: "درآمد دعوت",
    referralLast30: "۳۰ روز اخیر",
    referralRate: "سهم شما",
    referralPeople: "افرادی که معرفی کرده‌اید",
    referralActiveNote: "فعال یعنی حساب ایمیلش را تأیید کرده و می‌تواند استخراج کند. ثبت‌نام‌های تأییدنشده جداگانه شمرده می‌شوند.",
    referralHowTitle: "چطور کار می‌کند",
    referralHowBody: "هشریال ۲٪ از درآمد ماینر را به عنوان کارمزد برمی‌دارد. وقتی کسی با لینک شما ثبت‌نام کند، نیمی از همان کارمزد به شما می‌رسد — یعنی ۱٪ از هرچه استخراج می‌کند، تا هر زمان که فعال باشد. این مبلغ از سهم ما کم می‌شود، پس کسی که دعوت کرده‌اید دقیقاً همان ۹۸٪ همیشگی را دریافت می‌کند. پاداش‌ها روزانه محاسبه و به موجودی شما اضافه می‌شوند.",
    referralYourPeople: "معرفی‌های شما",
    referralNobodyYet: "هنوز کسی با لینک شما ثبت‌نام نکرده است. آن را به اشتراک بگذارید تا پاداش‌ها اینجا نمایش داده شوند.",
    referralUser: "کاربر",
    referralJoined: "تاریخ عضویت",
    referralActive: "فعال",
    referralPendingVerify: "تأییدنشده",
    sidebarReferral: "دعوت و درآمد",
    headerWelcome: "خوش آمدید",
    headerLive: "زنده",
    signOut: "خروج",
    darkMode: "حالت تاریک",
    lightMode: "حالت روشن",
    language: "زبان",
    loading: "در حال بارگذاری…",
    loading2: "در حال بارگذاری داشبورد…",
    loadingWorkers: "در حال بارگذاری کارگران…",
    noData: "داده‌ای وجود ندارد",
    noDataWorker: "هنوز داده‌ای برای این کارگر وجود ندارد.",
    errorPrefix: "⚠",

    dashboardTitle: "داشبورد",
    dashboardSub: "آمار زنده استخر · هر ۶۰ ثانیه به‌روز می‌شود",
    hashrate10m: "هش‌ریت (۱۰ دقیقه)",
    hashrate5m: "هش‌ریت (۵ دقیقه)",
    activeWorkers: "کارگران فعال",
    balance: "موجودی",
    last24h: "۲۴ ساعت اخیر",
    totalEarned: "کل درآمد",
    acceptance: "نرخ پذیرش",
    hashrateHistory: "تاریخچه هش‌ریت",
    noHashrateData: "داده هش‌ریتی وجود ندارد. ماینرهای خود را متصل کنید.",
    viewAll: "مشاهده همه ←",

    workersTitle: "کارگران",
    workersSub: "تمام ماینرهای متصل به حساب شما",
    total: "کل",
    online: "آنلاین",
    offline: "آفلاین",
    all: "همه",
    noWorkers: "هنوز کارگری وجود ندارد.",
    connectFirst: "اولین ماینر خود را متصل کنید ←",
    colWorker: "کارگر",
    colStatus: "وضعیت",
    col10m: "۱۰ دقیقه",
    col5m: "۵ دقیقه",
    col1h: "۱ ساعت",
    col24h: "۲۴ ساعت",
    colAccepted: "پذیرفته شده",
    colStale: "منسوخ",
    colLastSeen: "آخرین بازدید",

    workerDetail: "جزئیات کارگر",
    backToWorkers: "→ بازگشت به کارگران",
    workerNotFound: "کارگر یافت نشد.",
    hashrateChart: "هش‌ریت (۲۴ ساعت)",
    shares: "سهم‌ها",
    accepted: "پذیرفته شده",
    stale: "منسوخ",

    earningsTitle: "درآمد",
    earningsSub: "سوابق درآمد و پرداخت",
    date: "تاریخ",
    amount: "مقدار",
    pending: "در انتظار",
    processing: "در حال پردازش",
    completed: "انجام شد",
    failed: "ناموفق",
    address: "آدرس",
    txid: "تراکنش",
    records: "رکورد",
    balance2: "موجودی",
    earn24h: "درآمد ۲۴ ساعت",
    earnTotal: "کل درآمد",
    paidOut: "پرداخت شده",
    settleDate: "تاریخ تسویه",
    loadingEarnings: "در حال بارگذاری درآمد…",
    noEarnings: "داده درآمدی وجود ندارد.",
    requestPayout: "درخواست پرداخت",
    payoutHistory: "تاریخچه پرداخت",
    earningsHistory: "تاریخچه درآمد",
    submitting: "در حال ارسال",
    payoutMinNote: "مطمئن شوید که آدرس پرداخت را در تنظیمات وارد کرده‌اید.",
    accountInfo: "حساب",
    memberSince: "عضو از",
    settingsUserId: "شناسهٔ کاربری",
    page: "صفحه",
    of: "از",
    prev: "→ قبلی",
    next: "بعدی ←",

    connectTitle: "اتصال ماینرهای شما",
    connectIntro: "ASIC یا نرم‌افزار ماینینگ خود را با این تنظیمات پیکربندی کنید.",
    stratumHost: "هاست استراتوم",
    stratumPort: "پورت",
    workerLabel: "نام کارگر",
    workerExample: "WORKER_NAME را با هر برچسبی جایگزین کنید",
    poolFee: "کارمزد استخر ۲٪ اعمال می‌شود.",
    connectSteps: "دستورالعمل راه‌اندازی",
    step1: "وارد پنل مدیریت ماینر خود شوید",
    step2: "URL را به",
    step3: "Worker را به",
    step4: "ذخیره و ماینر را مجدداً راه‌اندازی کنید",
    note: "نکته",
    noteText: "ماینر شما ظرف ۶۰ ثانیه در صفحه کارگران ظاهر می‌شود.",
    whatTitle: "چه انتظاری دارید",
    what1: "هش‌ریت شما به صورت زنده در داشبورد نمایش داده می‌شود",
    what2: "هر کارگر جداگانه ردیابی می‌شود",
    what3: "درآمد هر ۶۰ ثانیه به‌روز می‌شود",
    what4: "پرداخت از ۰.۰۰۱ BTC امکان‌پذیر است",

    settingsTitle: "تنظیمات",
    settingsSub: "مدیریت تنظیمات حساب شما",
    bitcoinAddress: "آدرس بیت‌کوین",
    bitcoinAddressLabel: "آدرس پرداخت BTC شما",
    bitcoinAddressHint: "آدرس کیف پول بیت‌کوین خود را برای پرداخت وارد کنید.",
    save: "ذخیره",
    saved: "ذخیره شد!",
    notifications: "اعلان‌ها",
    notifyOffline: "هشدار قطعی کارگر",
    notifyOfflineDesc: "هنگام قطع شدن کارگر اعلان دریافت کنید",
    notifyHashrate: "هشدار کاهش هش‌ریت",
    notifyHashrateDesc: "هنگام کاهش قابل توجه هش‌ریت اعلان دریافت کنید",
    notifyThreshold: "آستانه کاهش (٪)",
    notifyThresholdDesc: "حداقل درصد کاهش برای فعال‌سازی اعلان",
    saving: "در حال ذخیره…",

    notifTitle: "اعلان‌ها",
    notifSub: "هشدارها و به‌روزرسانی‌های فعالیت ماینینگ شما",
    noNotifs: "هنوز اعلانی وجود ندارد.",
    markAllRead: "علامت‌گذاری همه به عنوان خوانده شده",
    unread: "خوانده نشده",
    notifWorkerOffline: "قطع شدن کارگر",
    notifWorkerOnline: "آنلاین شدن کارگر",
    notifHashrateDrop: "کاهش هش‌ریت",
    justNow: "همین حالا",
    minsAgo: "دقیقه پیش",
    hoursAgo: "ساعت پیش",
    daysAgo: "روز پیش",

    loginTitle: "ورود به حساب شما",
    loginEmail: "ایمیل",
    loginPassword: "رمز عبور",
    loginBtn: "ورود ←",
    loginNoAccount: "حساب ندارید؟",
    loginCreateOne: "ایجاد کنید ←",
    loginFailed: "ورود ناموفق",
    signingIn: "در حال ورود…",

    registerTitle: "ایجاد حساب",
    registerSub: "نام کاربری شما هویت شما در استخر است. با دقت انتخاب کنید.",
    registerUsername: "نام کاربری",
    registerUsernameHint: "۳-۲۰ کاراکتر، حروف کوچک/اعداد/خط زیرخط",
    registerEmail: "ایمیل",
    registerPassword: "رمز عبور",
    registerPasswordHint: "حداقل ۱۰ کاراکتر: حروف بزرگ، کوچک، عدد و نماد",
    hashing: "در حال ماینینگ",
    updated: "بروزرسانی",
    ago: "پیش",
    average: "میانگین",
    sharesAccepted: "سهم‌های پذیرفته",
    workerStatus: "وضعیت کارگرها",
    live: "زنده",
    faults: "خطاها",
    none: "هیچ",
    status: "وضعیت",
    registerCheckEmailTitle: "ایمیل خود را بررسی کنید",
    registerCheckEmailSub: "لینک تایید به ایمیل شما ارسال شد. برای تایید حساب روی آن کلیک کنید — در همین حین می‌توانید ماینینگ را شروع کنید.",
    registerContinueToDashboard: "ادامه به داشبورد \u2190",
    registerResendLink: "دریافت نکردید؟ ارسال دوباره",
    registerResending: "در حال ارسال…",
    registerResendSent: "ارسال شد. تا یک دقیقه دیگر می‌رسد.",
    registerResendWait: "کمی بعد دوباره تلاش کنید",
    registerResendFailed: "ارسال دوباره ناموفق بود. کمی بعد تلاش کنید.",
    registerCheckSpam: "اگر در صندوق ورودی نبود، پوشهٔ هرزنامه را بررسی کنید.",
    registerEmailUnavailableTitle: "حساب ساخته شد",
    registerEmailUnavailableSub: "در حال حاضر نتوانستیم ایمیل تأیید بفرستیم. حساب شما فعال است — برای تأیید با پشتیبانی تماس بگیرید.",
    btcPrice: "قیمت بیت‌کوین",
    backToHome: "بازگشت به hashrial.com",
    tenMinAverage: "میانگین ۱۰ دقیقه‌ای",
    fiveMinAverage: "میانگین ۵ دقیقه‌ای",
    chartCollecting: "در حال جمع‌آوری داده — نمودار با رسیدن نمونه‌ها کامل می‌شود.",
    degraded: "افت کرده",
    degradedHint: "افت کرده = کمتر از ۶۰٪ میانگین یک‌ساعتهٔ خودِ دستگاه.",
    perThDay: "به ازای هر TH در روز",
    showIn: "نمایش به",
    attentionCount: "{n} دستگاه نیاز به بررسی دارد",
    allRigsHealthy: "هر {n} دستگاه سالم است",
    poolFeeNote: "کارمزد استخر ۲٪ — یک سهم از هر ۵۰ سهم.",
    payoutProgress: "تا حداقل برداشت",
    payoutReady: "آمادهٔ برداشت",
    priceUnavailable: "قیمت در دسترس نیست",
    priceDelayed: "با تأخیر",
    priceDelayedHint: "خوراک قیمت لحظه‌ای در دسترس نیست — آخرین مقدار شناخته‌شده نمایش داده می‌شود.",
    currency: "ارز",
    exchangeAtSarraf: "تبدیل در صراف ←",
    rateFromSarraf: "نرخ صراف",
    rateFromSarrafHint: "نرخ از صراف تنظیم شده، نه از خوراک عمومی.",
    registerBtn: "ایجاد حساب ←",
    registerHasAccount: "قبلاً حساب دارید؟",
    registerSignIn: "ورود ←",
    registerFailed: "ثبت‌نام ناموفق",
    creatingAccount: "در حال ایجاد حساب…",
    registerUsernameError: "نام کاربری: ۳-۲۰ کاراکتر، حروف کوچک/اعداد/خط زیرخط",
    registerPasswordError: "رمز عبور باید حداقل ۱۰ کاراکتر باشد",
    registerTaken: "نام کاربری یا ایمیل قبلاً ثبت شده است",
    loginForgotPassword: "رمز عبور را فراموش کرده‌اید؟",
    forgotTitle: "بازیابی رمز عبور",
    forgotSub: "ایمیل خود را وارد کنید تا لینک بازیابی رمز عبور برایتان ارسال شود.",
    forgotEmail: "ایمیل",
    forgotBtn: "ارسال لینک بازیابی ←",
    forgotSending: "در حال ارسال…",
    forgotSent: "اگر حسابی با این ایمیل وجود داشته باشد، لینک بازیابی ارسال شد. صندوق ورودی خود را بررسی کنید.",
    forgotBackToLogin: "بازگشت به ورود",
    resetTitle: "رمز عبور جدید را انتخاب کنید",
    resetSub: "یک رمز عبور جدید برای حساب خود وارد کنید.",
    resetPassword: "رمز عبور جدید",
    resetConfirm: "تکرار رمز عبور",
    resetBtn: "بازنشانی رمز عبور ←",
    resetSubmitting: "در حال بازنشانی…",
    resetSuccess: "رمز عبور بازنشانی شد. اکنون می‌توانید با رمز جدید وارد شوید.",
    resetMismatch: "رمزهای عبور مطابقت ندارند",
    resetInvalidLink: "این لینک بازنشانی نامعتبر یا منقضی شده است.",
    resetGoToLogin: "ورود ←",
    verifyTitle: "در حال تایید ایمیل شما…",
    verifySuccess: "ایمیل شما تایید شد!",
    verifySuccessSub: "همه چیز آماده است.",
    verifyFailed: "این لینک تایید نامعتبر یا منقضی شده است.",
    verifyGoToDashboard: "رفتن به داشبورد ←",
    verifyGoToLogin: "ورود ←",
  },
  zh: {
    sidebarDashboard: "仪表板",
    sidebarWorkers: "矿工",
    sidebarEarnings: "收益",
    sidebarConnect: "连接矿机",
    sidebarNotifications: "通知",
    sidebarSettings: "设置",
    sidebarAdmin: "管理",
    adminTitle: "管理",
    adminSub: "矿池概览、提现与内容。此处的每个操作都会在服务端重新校验。",
    adminUsers: "用户",
    adminVerified: "已验证",
    adminReferred: "受邀",
    adminWorkers: "在线矿机",
    adminOf: "/",
    adminPayoutsPending: "待处理提现",
    adminReferralPaid: "已付推荐奖励",
    adminCredits: "条记录",
    adminPayouts: "提现请求",
    adminNoPayouts: "暂无提现请求。",
    adminMarkPaid: "标记已付",
    adminMarkFailed: "标记失败",
    adminConfirmStatus: "更改提现状态",
    adminTxidPrompt: "交易 ID（可选）：",
    adminArticles: "文章",
    adminPublished: "已发布",
    adminDrafts: "草稿",
    adminArticle: "文章",
    adminWords: "字数",
    adminArticlesNote: "文章以 markdown 存放在 content/blog/。发布需要编辑文件并部署，这是刻意设计——git 历史即审计记录，而能从网页表单发布的文章，也就是能在未读事实核查清单的情况下发布的文章。",
    adminRecentUsers: "最近用户",
    adminReferredBy: "推荐人",
    adminForbiddenHint: "此页面需要管理员账户。请将您的用户 ID 添加到 wrangler.toml 的 ADMIN_USER_IDS。",
    blogTitle: "博客",
    blogSub: "挖矿、矿池与比特币——写给真正在运行矿机的人。",
    blogEmpty: "尚未发布文章。",
    blogMinRead: "分钟阅读",
    blogAll: "全部文章",
    blogAlsoIn: "其他语言：",
    blogCtaTitle: "开始在 Hashrial 挖矿",
    blogCtaBody: "2% 固定费率，98% 归您，并为难以连接大型矿池的网络环境而设计的代理。",
    blogCtaAction: "创建账户 →",
    navBlog: "博客",
    referralTitle: "推荐赚取",
    referralSub: "您推荐的人挖矿收益的 1% 归您——由我们的手续费支付，而非他们的收益。",
    referralYourLink: "您的推荐链接",
    referralCopy: "复制链接",
    referralCopied: "已复制",
    referralCodeIs: "您的推荐码：",
    referralChooseCode: "选择您的专属推荐码",
    referralCodePlaceholder: "例如 SINA",
    referralClaimCode: "认领",
    referralSaving: "保存中…",
    referralCodeHint: "4–16 位字母和数字。选一个容易说出口的。",
    referralCodeKeepsWorking: "您已分享的链接仍然有效——认领过的每个推荐码都永久归您所有。",
    referralCodeSaved: "推荐码已更新",
    referralCodeTaken: "该推荐码已被占用",
    referralCodeReserved: "该推荐码为系统保留",
    referralCodeInvalid: "仅限 4–16 位字母和数字",
    referralCodeFailed: "保存失败，请重试。",
    referralEarned: "推荐收益",
    referralLast30: "最近 30 天",
    referralRate: "您的比例",
    referralPeople: "您推荐的人",
    referralActiveNote: "“活跃”指账户已验证邮箱且可以挖矿。未验证的注册单独统计。",
    referralHowTitle: "运作方式",
    referralHowBody: "Hashrial 收取矿工收益的 2% 作为手续费。当有人通过您的链接注册时，您将获得该手续费的一半——即他们所挖收益的 1%，只要他们持续挖矿。这笔钱来自我们的份额，因此您邀请的人仍然完整保留 98%。奖励每日结算并计入您的余额。",
    referralYourPeople: "您的推荐",
    referralNobodyYet: "还没有人通过您的链接注册。分享链接后，奖励将显示在这里。",
    referralUser: "用户",
    referralJoined: "加入时间",
    referralActive: "活跃",
    referralPendingVerify: "未验证",
    sidebarReferral: "推荐赚取",
    headerWelcome: "欢迎回来",
    headerLive: "实时",
    signOut: "登出",
    darkMode: "深色模式",
    lightMode: "浅色模式",
    language: "语言",
    loading: "加载中…",
    loading2: "加载仪表板…",
    loadingWorkers: "加载矿工…",
    noData: "暂无数据",
    noDataWorker: "该矿工暂无数据。",
    errorPrefix: "⚠",

    dashboardTitle: "仪表板",
    dashboardSub: "实时矿池数据 · 每60秒更新",
    hashrate10m: "算力 (10分钟)",
    hashrate5m: "算力 (5分钟)",
    activeWorkers: "活跃矿工",
    balance: "余额",
    last24h: "最近24小时",
    totalEarned: "总收入",
    acceptance: "接受率",
    hashrateHistory: "算力历史",
    noHashrateData: "暂无算力数据。请连接您的矿机。",
    viewAll: "查看全部 →",

    workersTitle: "矿工",
    workersSub: "连接到您账户的所有矿机",
    total: "总计",
    online: "在线",
    offline: "离线",
    all: "全部",
    noWorkers: "暂无矿工。",
    connectFirst: "连接您的第一台矿机 →",
    colWorker: "矿工",
    colStatus: "状态",
    col10m: "10分钟",
    col5m: "5分钟",
    col1h: "1小时",
    col24h: "24小时",
    colAccepted: "已接受",
    colStale: "过期",
    colLastSeen: "最近在线",

    workerDetail: "矿工详情",
    backToWorkers: "← 返回矿工列表",
    workerNotFound: "未找到矿工。",
    hashrateChart: "算力 (24小时)",
    shares: "份额",
    accepted: "已接受",
    stale: "过期",

    earningsTitle: "收益",
    earningsSub: "历史收益和支付记录",
    date: "日期",
    amount: "金额",
    pending: "待处理",
    processing: "处理中",
    completed: "已完成",
    failed: "失败",
    address: "地址",
    txid: "交易",
    records: "条记录",
    balance2: "余额",
    earn24h: "24小时收益",
    earnTotal: "总收入",
    paidOut: "已支付",
    settleDate: "结算日期",
    loadingEarnings: "加载收益…",
    noEarnings: "暂无收益数据。",
    requestPayout: "申请提现",
    payoutHistory: "提现记录",
    earningsHistory: "收益历史",
    submitting: "提交中",
    payoutMinNote: "请确保您已在设置中填写了付款地址。",
    accountInfo: "账户",
    memberSince: "注册时间",
    settingsUserId: "用户 ID",
    page: "第",
    of: "页，共",
    prev: "← 上一页",
    next: "下一页 →",

    connectTitle: "连接您的矿机",
    connectIntro: "使用以下设置配置您的ASIC或挖矿软件。",
    stratumHost: "Stratum 主机",
    stratumPort: "端口",
    workerLabel: "矿工名称",
    workerExample: "将 WORKER_NAME 替换为任意标签",
    poolFee: "矿池费率 2%。",
    connectSteps: "设置步骤",
    step1: "登录矿机管理面板",
    step2: "设置 URL 为",
    step3: "设置 Worker 为",
    step4: "保存并重启矿机",
    note: "提示",
    noteText: "矿机连接后60秒内会出现在矿工页面。",
    whatTitle: "您可以期待",
    what1: "算力实时显示在仪表板上",
    what2: "每台矿机单独跟踪",
    what3: "收益每60秒更新",
    what4: "余额超过 0.001 BTC 可提现",

    settingsTitle: "设置",
    settingsSub: "管理您的账户偏好",
    bitcoinAddress: "比特币地址",
    bitcoinAddressLabel: "您的 BTC 收款地址",
    bitcoinAddressHint: "输入您的比特币钱包地址用于收款。",
    save: "保存",
    saved: "已保存！",
    notifications: "通知",
    notifyOffline: "矿机离线提醒",
    notifyOfflineDesc: "矿机离线时接收通知",
    notifyHashrate: "算力下降提醒",
    notifyHashrateDesc: "算力显著下降时接收通知",
    notifyThreshold: "下降阈值 (%)",
    notifyThresholdDesc: "触发提醒的最小下降百分比",
    saving: "保存中…",

    notifTitle: "通知",
    notifSub: "关于挖矿活动的提醒和更新",
    noNotifs: "暂无通知。",
    markAllRead: "全部标记为已读",
    unread: "未读",
    notifWorkerOffline: "矿机离线",
    notifWorkerOnline: "矿机在线",
    notifHashrateDrop: "算力下降",
    justNow: "刚刚",
    minsAgo: "分钟前",
    hoursAgo: "小时前",
    daysAgo: "天前",

    loginTitle: "登录您的账户",
    loginEmail: "邮箱",
    loginPassword: "密码",
    loginBtn: "登录 →",
    loginNoAccount: "没有账户？",
    loginCreateOne: "创建 →",
    loginFailed: "登录失败",
    signingIn: "登录中…",

    registerTitle: "创建账户",
    registerSub: "您的用户名是您的矿池身份。请谨慎选择，不可更改。",
    registerUsername: "用户名",
    registerUsernameHint: "3-20个字符，小写字母/数字/下划线",
    registerEmail: "邮箱",
    registerPassword: "密码",
    registerPasswordHint: "至少10个字符：含大小写字母、数字和符号",
    hashing: "挖矿中",
    updated: "更新于",
    ago: "前",
    average: "平均",
    sharesAccepted: "已接受份额",
    workerStatus: "矿机状态",
    live: "实时",
    faults: "故障",
    none: "无",
    status: "状态",
    registerCheckEmailTitle: "请查收邮件",
    registerCheckEmailSub: "我们已向您的邮箱发送了验证链接。点击链接确认您的账户——与此同时您可以开始挖矿。",
    registerContinueToDashboard: "前往仪表盘 \u2192",
    registerResendLink: "没有收到？重新发送",
    registerResending: "发送中…",
    registerResendSent: "已发送，约一分钟内送达。",
    registerResendWait: "请稍后再重新发送",
    registerResendFailed: "重新发送失败，请稍后再试。",
    registerCheckSpam: "如果收件箱中没有，请检查垃圾邮件文件夹。",
    registerEmailUnavailableTitle: "账户已创建",
    registerEmailUnavailableSub: "目前无法发送验证邮件。您的账户可以使用——请联系客服完成验证。",
    btcPrice: "比特币价格",
    backToHome: "返回 hashrial.com",
    tenMinAverage: "10 分钟平均值",
    fiveMinAverage: "5 分钟平均值",
    chartCollecting: "正在收集数据——随着样本到达图表会逐步填充。",
    degraded: "性能下降",
    degradedHint: "性能下降 = 低于该矿机自身 1 小时平均值的 60%。",
    perThDay: "每 TH/天",
    showIn: "显示为",
    attentionCount: "{n} 台矿机需要处理",
    allRigsHealthy: "全部 {n} 台矿机正常",
    poolFeeNote: "矿池费率 2% — 每 50 份额收取 1 份。",
    payoutProgress: "距最低提现额",
    payoutReady: "可以提现",
    priceUnavailable: "价格不可用",
    priceDelayed: "延迟",
    priceDelayedHint: "实时价格源不可用——显示最后已知价格。",
    currency: "货币",
    exchangeAtSarraf: "在 صراف 兑换 →",
    rateFromSarraf: "صراف 汇率",
    rateFromSarrafHint: "汇率取自 صراف，而非公共数据源。",
    registerBtn: "创建账户 →",
    registerHasAccount: "已有账户？",
    registerSignIn: "登录 →",
    registerFailed: "注册失败",
    creatingAccount: "创建账户中…",
    registerUsernameError: "用户名：3-20个字符，小写字母/数字/下划线",
    registerPasswordError: "密码至少需要10个字符",
    registerTaken: "用户名或邮箱已被使用",
    loginForgotPassword: "忘记密码？",
    forgotTitle: "重置密码",
    forgotSub: "输入您的邮箱，我们会发送重置密码的链接。",
    forgotEmail: "邮箱",
    forgotBtn: "发送重置链接 →",
    forgotSending: "发送中…",
    forgotSent: "如果该邮箱存在账户，重置链接已发送。请查看您的收件箱。",
    forgotBackToLogin: "返回登录",
    resetTitle: "设置新密码",
    resetSub: "为您的账户输入新密码。",
    resetPassword: "新密码",
    resetConfirm: "确认密码",
    resetBtn: "重置密码 →",
    resetSubmitting: "重置中…",
    resetSuccess: "密码已重置。您现在可以使用新密码登录。",
    resetMismatch: "两次密码不一致",
    resetInvalidLink: "此重置链接无效或已过期。",
    resetGoToLogin: "前往登录 →",
    verifyTitle: "正在验证您的邮箱…",
    verifySuccess: "邮箱验证成功！",
    verifySuccessSub: "一切就绪。",
    verifyFailed: "此验证链接无效或已过期。",
    verifyGoToDashboard: "前往仪表盘 →",
    verifyGoToLogin: "前往登录 →",
  },
  ru: {
    sidebarDashboard: "Панель",
    sidebarWorkers: "Майнеры",
    sidebarEarnings: "Доход",
    sidebarConnect: "Подключение",
    sidebarNotifications: "Уведомления",
    sidebarSettings: "Настройки",
    sidebarAdmin: "Админ",
    adminTitle: "Админ",
    adminSub: "Обзор пула, выплаты и контент. Каждое действие перепроверяется на сервере.",
    adminUsers: "Пользователи",
    adminVerified: "подтверждено",
    adminReferred: "по рефералам",
    adminWorkers: "Воркеры онлайн",
    adminOf: "из",
    adminPayoutsPending: "Выплаты в ожидании",
    adminReferralPaid: "Выплачено по рефералам",
    adminCredits: "начислений",
    adminPayouts: "Запросы на выплату",
    adminNoPayouts: "Запросов на выплату пока нет.",
    adminMarkPaid: "Отметить выплаченным",
    adminMarkFailed: "Отметить неудачным",
    adminConfirmStatus: "Изменить статус выплаты",
    adminTxidPrompt: "ID транзакции (необязательно):",
    adminArticles: "Статьи",
    adminPublished: "опубликовано",
    adminDrafts: "черновик",
    adminArticle: "Статья",
    adminWords: "Слов",
    adminArticlesNote: "Статьи хранятся как markdown в content/blog/. Публикация намеренно требует правки файла и деплоя — история git служит журналом аудита, а статья, публикуемая из веб-формы, публикуется без прочтения списка фактчекинга.",
    adminRecentUsers: "Недавние пользователи",
    adminReferredBy: "Пригласил",
    adminForbiddenHint: "Нужен аккаунт администратора. Добавьте свой ID в ADMIN_USER_IDS в wrangler.toml.",
    blogTitle: "Блог",
    blogSub: "Майнинг, пулы и биткоин — для тех, у кого реально стоит оборудование.",
    blogEmpty: "Статей пока нет.",
    blogMinRead: "мин чтения",
    blogAll: "Все статьи",
    blogAlsoIn: "Также доступно на:",
    blogCtaTitle: "Начните майнить с Hashrial",
    blogCtaBody: "Фиксированная комиссия 2%, 98% вам, и прокси, рассчитанный на сети, где крупные пулы труднодоступны.",
    blogCtaAction: "Создать аккаунт →",
    navBlog: "Блог",
    referralTitle: "Приглашайте и зарабатывайте",
    referralSub: "Получайте 1% от добычи приглашённых — из нашей комиссии, а не из их дохода.",
    referralYourLink: "Ваша реферальная ссылка",
    referralCopy: "Копировать",
    referralCopied: "Скопировано",
    referralCodeIs: "Ваш код:",
    referralChooseCode: "Выберите свой код",
    referralCodePlaceholder: "например SINA",
    referralClaimCode: "Занять",
    referralSaving: "Сохранение…",
    referralCodeHint: "4–16 букв и цифр. Выберите то, что легко произнести.",
    referralCodeKeepsWorking: "Уже отправленные ссылки продолжат работать — каждый занятый код остаётся вашим навсегда.",
    referralCodeSaved: "Код обновлён",
    referralCodeTaken: "Этот код уже занят",
    referralCodeReserved: "Этот код зарезервирован",
    referralCodeInvalid: "Только 4–16 букв и цифр",
    referralCodeFailed: "Не удалось сохранить. Попробуйте ещё раз.",
    referralEarned: "Реферальный доход",
    referralLast30: "За 30 дней",
    referralRate: "Ваша доля",
    referralPeople: "Приглашённые вами",
    referralActiveNote: "Активный — аккаунт подтвердил почту и может майнить. Неподтверждённые считаются отдельно.",
    referralHowTitle: "Как это работает",
    referralHowBody: "Hashrial удерживает комиссию 2% с дохода майнера. Когда кто-то регистрируется по вашей ссылке, вы получаете половину этой комиссии — 1% от всего, что он добывает, пока он майнит. Эта сумма вычитается из нашей доли, поэтому приглашённый вами человек по-прежнему получает свои 98%. Вознаграждения начисляются ежедневно.",
    referralYourPeople: "Ваши рефералы",
    referralNobodyYet: "По вашей ссылке ещё никто не зарегистрировался. Поделитесь ей, и вознаграждения появятся здесь.",
    referralUser: "Пользователь",
    referralJoined: "Регистрация",
    referralActive: "Активен",
    referralPendingVerify: "Не подтверждён",
    sidebarReferral: "Приглашайте",
    headerWelcome: "С возвращением",
    headerLive: "В прямом эфире",
    signOut: "Выйти",
    darkMode: "Тёмная тема",
    lightMode: "Светлая тема",
    language: "Язык",
    loading: "Загрузка…",
    loading2: "Загрузка панели…",
    loadingWorkers: "Загрузка майнеров…",
    noData: "Нет данных",
    noDataWorker: "Для этого работника пока нет данных.",
    errorPrefix: "⚠",

    dashboardTitle: "Панель управления",
    dashboardSub: "Статистика пула · обновляется каждые 60 с",
    hashrate10m: "Хешрейт (10 мин)",
    hashrate5m: "Хешрейт (5 мин)",
    activeWorkers: "Активные майнеры",
    balance: "Баланс",
    last24h: "За 24 часа",
    totalEarned: "Всего заработано",
    acceptance: "Принятие",
    hashrateHistory: "История хешрейта",
    noHashrateData: "Нет данных. Подключите майнеры.",
    viewAll: "Все →",

    workersTitle: "Майнеры",
    workersSub: "Все майнеры, подключённые к вашему аккаунту",
    total: "Всего",
    online: "онлайн",
    offline: "офлайн",
    all: "все",
    noWorkers: "Пока нет майнеров.",
    connectFirst: "Подключить первый майнер →",
    colWorker: "Майнер",
    colStatus: "Статус",
    col10m: "10 мин",
    col5m: "5 мин",
    col1h: "1 час",
    col24h: "24 часа",
    colAccepted: "Принято",
    colStale: "Устарело",
    colLastSeen: "Последний раз",

    workerDetail: "Детали майнера",
    backToWorkers: "← Назад к майнерам",
    workerNotFound: "Майнер не найден.",
    hashrateChart: "Хешрейт (24 часа)",
    shares: "Доли",
    accepted: "Принято",
    stale: "Устарело",

    earningsTitle: "Доход",
    earningsSub: "История доходов и выплат",
    date: "Дата",
    amount: "Сумма",
    pending: "В ожидании",
    processing: "Обработка",
    completed: "Выполнено",
    failed: "Ошибка",
    address: "Адрес",
    txid: "Транзакция",
    records: "записей",
    balance2: "Баланс",
    earn24h: "Доход за 24ч",
    earnTotal: "Всего заработано",
    paidOut: "Выплачено",
    settleDate: "Дата расчёта",
    loadingEarnings: "Загрузка доходов…",
    noEarnings: "Нет данных о доходах.",
    requestPayout: "Запросить выплату",
    payoutHistory: "История выплат",
    earningsHistory: "История доходов",
    submitting: "Отправка",
    payoutMinNote: "Убедитесь, что вы указали адрес для выплаты в настройках.",
    accountInfo: "Аккаунт",
    memberSince: "Дата регистрации",
    settingsUserId: "ID пользователя",
    page: "Стр.",
    of: "из",
    prev: "← Назад",
    next: "Далее →",

    connectTitle: "Подключение майнеров",
    connectIntro: "Настройте ASIC или ПО с этими параметрами.",
    stratumHost: "Хост Stratum",
    stratumPort: "Порт",
    workerLabel: "Имя майнера",
    workerExample: "Замените WORKER_NAME на любую метку",
    poolFee: "Комиссия пула 2%.",
    connectSteps: "Инструкция",
    step1: "Войдите в панель управления майнера",
    step2: "Установите URL",
    step3: "Установите Worker",
    step4: "Сохраните и перезапустите майнер",
    note: "Примечание",
    noteText: "Майнер появится на странице Workers через 60 секунд.",
    whatTitle: "Что вы получите",
    what1: "Хешрейт отображается в реальном времени",
    what2: "Каждый майнер отслеживается отдельно",
    what3: "Доход обновляется каждые 60 секунд",
    what4: "Выплаты доступны от 0.001 BTC",

    settingsTitle: "Настройки",
    settingsSub: "Управление настройками аккаунта",
    bitcoinAddress: "Биткоин-адрес",
    bitcoinAddressLabel: "Ваш BTC адрес для выплат",
    bitcoinAddressHint: "Введите адрес кошелька для выплат.",
    save: "Сохранить",
    saved: "Сохранено!",
    notifications: "Уведомления",
    notifyOffline: "Оповещения об отключении",
    notifyOfflineDesc: "Получать уведомления при отключении майнера",
    notifyHashrate: "Оповещения о падении хешрейта",
    notifyHashrateDesc: "Получать уведомления при падении хешрейта",
    notifyThreshold: "Порог падения (%)",
    notifyThresholdDesc: "Минимальный процент падения для оповещения",
    saving: "Сохранение…",

    notifTitle: "Уведомления",
    notifSub: "Оповещения о вашей майнинговой активности",
    noNotifs: "Пока нет уведомлений.",
    markAllRead: "Отметить всё прочитанным",
    unread: "непрочитанные",
    notifWorkerOffline: "Майнер отключён",
    notifWorkerOnline: "Майнер онлайн",
    notifHashrateDrop: "Падение хешрейта",
    justNow: "только что",
    minsAgo: "мин. назад",
    hoursAgo: "ч. назад",
    daysAgo: "д. назад",

    loginTitle: "Вход в аккаунт",
    loginEmail: "Эл. почта",
    loginPassword: "Пароль",
    loginBtn: "Войти →",
    loginNoAccount: "Нет аккаунта?",
    loginCreateOne: "Создать →",
    loginFailed: "Ошибка входа",
    signingIn: "Вход…",

    registerTitle: "Создать аккаунт",
    registerSub: "Ваше имя пользователя — ваша идентичность в пуле.",
    registerUsername: "Имя пользователя",
    registerUsernameHint: "3–20 символов, буквы/цифры/подчёркивание",
    registerEmail: "Эл. почта",
    registerPassword: "Пароль",
    registerPasswordHint: "Мин. 10 симв.: загл., строч. буква, цифра и символ",
    hashing: "Майнинг",
    updated: "Обновлено",
    ago: "назад",
    average: "средн.",
    sharesAccepted: "Принято шар",
    workerStatus: "Статус воркеров",
    live: "Онлайн",
    faults: "Сбои",
    none: "Нет",
    status: "Статус",
    registerCheckEmailTitle: "Проверьте почту",
    registerCheckEmailSub: "Мы отправили ссылку для подтверждения на ваш email. Перейдите по ней, чтобы подтвердить аккаунт — тем временем вы можете начать майнинг.",
    registerContinueToDashboard: "Перейти в панель \u2192",
    registerResendLink: "Не пришло? Отправить ещё раз",
    registerResending: "Отправка…",
    registerResendSent: "Отправлено. Придёт в течение минуты.",
    registerResendWait: "Повторная отправка будет доступна скоро",
    registerResendFailed: "Не удалось отправить. Попробуйте позже.",
    registerCheckSpam: "Если письма нет во входящих, проверьте папку со спамом.",
    registerEmailUnavailableTitle: "Аккаунт создан",
    registerEmailUnavailableSub: "Сейчас мы не смогли отправить письмо для подтверждения. Аккаунт работает — обратитесь в поддержку.",
    btcPrice: "Цена биткоина",
    backToHome: "Вернуться на hashrial.com",
    tenMinAverage: "Среднее за 10 минут",
    fiveMinAverage: "Среднее за 5 минут",
    chartCollecting: "Сбор данных — график заполнится по мере поступления замеров.",
    degraded: "Снижен",
    degradedHint: "Снижен = ниже 60% собственного часового среднего.",
    perThDay: "За TH/день",
    showIn: "Показать в",
    attentionCount: "{n} устройств требуют внимания",
    allRigsHealthy: "Все {n} устройств исправны",
    poolFeeNote: "Комиссия пула 2% — 1 доля из каждых 50.",
    payoutProgress: "До минимума вывода",
    payoutReady: "Готово к выводу",
    priceUnavailable: "Цена недоступна",
    priceDelayed: "С задержкой",
    priceDelayedHint: "Источник цен недоступен — показано последнее известное значение.",
    currency: "Валюта",
    exchangeAtSarraf: "Обменять в صراف →",
    rateFromSarraf: "курс صراف",
    rateFromSarrafHint: "Курс взят из صراف, а не из публичного источника.",
    registerBtn: "Создать аккаунт →",
    registerHasAccount: "Уже есть аккаунт?",
    registerSignIn: "Войти →",
    registerFailed: "Ошибка регистрации",
    creatingAccount: "Создание аккаунта…",
    registerUsernameError: "Имя: 3-20 символов, строчные буквы/цифры/подчёркивание",
    registerPasswordError: "Пароль должен быть не менее 10 символов",
    registerTaken: "Имя или почта уже заняты",
    loginForgotPassword: "Забыли пароль?",
    forgotTitle: "Сброс пароля",
    forgotSub: "Введите email, и мы отправим ссылку для сброса пароля.",
    forgotEmail: "Email",
    forgotBtn: "Отправить ссылку →",
    forgotSending: "Отправка…",
    forgotSent: "Если аккаунт с этим email существует, ссылка на сброс уже отправлена. Проверьте почту.",
    forgotBackToLogin: "Назад ко входу",
    resetTitle: "Новый пароль",
    resetSub: "Введите новый пароль для вашего аккаунта.",
    resetPassword: "Новый пароль",
    resetConfirm: "Подтвердите пароль",
    resetBtn: "Сбросить пароль →",
    resetSubmitting: "Сброс…",
    resetSuccess: "Пароль сброшен. Теперь вы можете войти с новым паролем.",
    resetMismatch: "Пароли не совпадают",
    resetInvalidLink: "Эта ссылка недействительна или истекла.",
    resetGoToLogin: "Войти →",
    verifyTitle: "Проверка вашего email…",
    verifySuccess: "Email подтверждён!",
    verifySuccessSub: "Всё готово.",
    verifyFailed: "Эта ссылка подтверждения недействительна или истекла.",
    verifyGoToDashboard: "Перейти в панель →",
    verifyGoToLogin: "Войти →",
  },
  es: {
    sidebarDashboard: "Panel",
    sidebarWorkers: "Trabajadores",
    sidebarEarnings: "Ganancias",
    sidebarConnect: "Conectar",
    sidebarNotifications: "Notificaciones",
    sidebarSettings: "Ajustes",
    sidebarAdmin: "Admin",
    adminTitle: "Admin",
    adminSub: "Resumen del pool, pagos y contenido. Cada acción se vuelve a verificar en el servidor.",
    adminUsers: "Usuarios",
    adminVerified: "verificados",
    adminReferred: "referidos",
    adminWorkers: "Equipos en línea",
    adminOf: "de",
    adminPayoutsPending: "Pagos pendientes",
    adminReferralPaid: "Recompensas pagadas",
    adminCredits: "créditos",
    adminPayouts: "Solicitudes de pago",
    adminNoPayouts: "Aún no hay solicitudes de pago.",
    adminMarkPaid: "Marcar pagado",
    adminMarkFailed: "Marcar fallido",
    adminConfirmStatus: "Cambiar estado del pago",
    adminTxidPrompt: "ID de transacción (opcional):",
    adminArticles: "Artículos",
    adminPublished: "publicados",
    adminDrafts: "borrador",
    adminArticle: "Artículo",
    adminWords: "Palabras",
    adminArticlesNote: "Los artículos son markdown en content/blog/. Publicar requiere editar el archivo y desplegar, a propósito: el historial de git es la auditoría, y un artículo publicable desde un formulario web es uno publicable sin leer su lista de verificación.",
    adminRecentUsers: "Usuarios recientes",
    adminReferredBy: "Referido por",
    adminForbiddenHint: "Esta página requiere una cuenta de administrador. Añade tu ID de usuario a ADMIN_USER_IDS en wrangler.toml.",
    blogTitle: "Blog",
    blogSub: "Minería, pools y Bitcoin — escrito para quien realmente tiene equipos funcionando.",
    blogEmpty: "Aún no hay artículos publicados.",
    blogMinRead: "min de lectura",
    blogAll: "Todos los artículos",
    blogAlsoIn: "También disponible en:",
    blogCtaTitle: "Empieza a minar con Hashrial",
    blogCtaBody: "Comisión fija del 2%, el 98% para ti, y un proxy pensado para redes donde los grandes pools son difíciles de alcanzar.",
    blogCtaAction: "Crear una cuenta →",
    navBlog: "Blog",
    referralTitle: "Refiere y gana",
    referralSub: "Gana el 1% de lo que minen las personas que traigas — sale de nuestra comisión, no de la suya.",
    referralYourLink: "Tu enlace de referido",
    referralCopy: "Copiar enlace",
    referralCopied: "Copiado",
    referralCodeIs: "Tu código:",
    referralChooseCode: "Elige tu propio código",
    referralCodePlaceholder: "p. ej. SINA",
    referralClaimCode: "Reservar",
    referralSaving: "Guardando…",
    referralCodeHint: "4–16 letras y números. Elige algo fácil de decir en voz alta.",
    referralCodeKeepsWorking: "Los enlaces que ya compartiste seguirán funcionando: cada código que reserves es tuyo para siempre.",
    referralCodeSaved: "Código actualizado",
    referralCodeTaken: "Ese código ya está en uso",
    referralCodeReserved: "Ese código está reservado",
    referralCodeInvalid: "Usa solo 4–16 letras y números",
    referralCodeFailed: "No se pudo guardar. Inténtalo de nuevo.",
    referralEarned: "Ganancias por referidos",
    referralLast30: "Últimos 30 días",
    referralRate: "Tu parte",
    referralPeople: "Personas que has traído",
    referralActiveNote: "Activo significa que la cuenta verificó su correo y puede minar. Los registros sin verificar se cuentan aparte.",
    referralHowTitle: "Cómo funciona",
    referralHowBody: "Hashrial cobra una comisión del 2% sobre lo que gana un minero. Cuando alguien se registra con tu enlace, recibes la mitad de esa comisión — el 1% de todo lo que mine, mientras siga minando. Sale de nuestra parte, así que la persona que invitaste conserva su 98% igual que siempre. Las recompensas se acreditan a diario.",
    referralYourPeople: "Tus referidos",
    referralNobodyYet: "Nadie se ha registrado con tu enlace todavía. Compártelo y tus recompensas aparecerán aquí.",
    referralUser: "Usuario",
    referralJoined: "Se unió",
    referralActive: "Activo",
    referralPendingVerify: "Sin verificar",
    sidebarReferral: "Refiere y gana",
    headerWelcome: "Bienvenido de nuevo",
    headerLive: "En vivo",
    signOut: "Cerrar sesión",
    darkMode: "Modo oscuro",
    lightMode: "Modo claro",
    language: "Idioma",
    loading: "Cargando…",
    loading2: "Cargando panel…",
    loadingWorkers: "Cargando trabajadores…",
    noData: "Sin datos",
    noDataWorker: "Sin datos aún para este trabajador.",
    errorPrefix: "⚠",

    dashboardTitle: "Panel",
    dashboardSub: "Estadísticas del pool · se actualiza cada 60s",
    hashrate10m: "Hashrate (10m)",
    hashrate5m: "Hashrate (5m)",
    activeWorkers: "Trabajadores activos",
    balance: "Saldo",
    last24h: "Últimas 24h",
    totalEarned: "Total ganado",
    acceptance: "Aceptación",
    hashrateHistory: "Historial de hashrate",
    noHashrateData: "Sin datos. Conecta tus mineros.",
    viewAll: "Ver todo →",

    workersTitle: "Trabajadores",
    workersSub: "Todos los mineros conectados a tu cuenta",
    total: "Total",
    online: "en línea",
    offline: "fuera de línea",
    all: "todos",
    noWorkers: "Aún no hay trabajadores.",
    connectFirst: "Conecta tu primer minero →",
    colWorker: "Trabajador",
    colStatus: "Estado",
    col10m: "10m",
    col5m: "5m",
    col1h: "1h",
    col24h: "24h",
    colAccepted: "Aceptado",
    colStale: "Obsoleto",
    colLastSeen: "Última vez",

    workerDetail: "Detalle del trabajador",
    backToWorkers: "← Volver a trabajadores",
    workerNotFound: "Trabajador no encontrado.",
    hashrateChart: "Hashrate (24h)",
    shares: "Acciones",
    accepted: "Aceptado",
    stale: "Obsoleto",

    earningsTitle: "Ganancias",
    earningsSub: "Historial de ganancias y pagos",
    date: "Fecha",
    amount: "Importe",
    pending: "Pendiente",
    processing: "Procesando",
    completed: "Completado",
    failed: "Fallido",
    address: "Dirección",
    txid: "Transacción",
    records: "registros",
    balance2: "Saldo",
    earn24h: "Ganancias 24h",
    earnTotal: "Total ganado",
    paidOut: "Pagado",
    settleDate: "Fecha de liquidación",
    loadingEarnings: "Cargando ganancias…",
    noEarnings: "Sin datos de ganancias.",
    requestPayout: "Solicitar Pago",
    payoutHistory: "Historial de Pagos",
    earningsHistory: "Historial de Ganancias",
    submitting: "Enviando",
    payoutMinNote: "Asegúrese de haber configurado una dirección de pago en Ajustes.",
    accountInfo: "Cuenta",
    memberSince: "Miembro desde",
    settingsUserId: "ID de usuario",
    page: "Pág.",
    of: "de",
    prev: "← Anterior",
    next: "Siguiente →",

    connectTitle: "Conecta tus mineros",
    connectIntro: "Configura tu ASIC o software con estos ajustes.",
    stratumHost: "Host Stratum",
    stratumPort: "Puerto",
    workerLabel: "Nombre del trabajador",
    workerExample: "Reemplaza WORKER_NAME con cualquier etiqueta",
    poolFee: "Comisión del pool 2%.",
    connectSteps: "Instrucciones",
    step1: "Accede al panel de administración del minero",
    step2: "Establece URL a",
    step3: "Establece Worker a",
    step4: "Guarda y reinicia el minero",
    note: "Nota",
    noteText: "Tu minero aparecerá en Workers en 60 segundos.",
    whatTitle: "Qué esperar",
    what1: "Tu hashrate aparece en vivo en el Panel",
    what2: "Cada trabajador se rastrea por separado",
    what3: "Las ganancias se actualizan cada 60s",
    what4: "Pagos disponibles desde 0.001 BTC",

    settingsTitle: "Ajustes",
    settingsSub: "Administra tus preferencias",
    bitcoinAddress: "Dirección Bitcoin",
    bitcoinAddressLabel: "Tu dirección BTC para pagos",
    bitcoinAddressHint: "Introduce tu dirección de billetera Bitcoin.",
    save: "Guardar",
    saved: "¡Guardado!",
    notifications: "Notificaciones",
    notifyOffline: "Alertas de trabajador desconectado",
    notifyOfflineDesc: "Notificar cuando un trabajador se desconecte",
    notifyHashrate: "Alertas de caída de hashrate",
    notifyHashrateDesc: "Notificar cuando el hashrate baje significativamente",
    notifyThreshold: "Umbral de caída (%)",
    notifyThresholdDesc: "Porcentaje mínimo de caída para alertar",
    saving: "Guardando…",

    notifTitle: "Notificaciones",
    notifSub: "Alertas sobre tu actividad de minería",
    noNotifs: "Sin notificaciones.",
    markAllRead: "Marcar todo como leído",
    unread: "sin leer",
    notifWorkerOffline: "Trabajador desconectado",
    notifWorkerOnline: "Trabajador en línea",
    notifHashrateDrop: "Caída de hashrate",
    justNow: "justo ahora",
    minsAgo: "min. atrás",
    hoursAgo: "h atrás",
    daysAgo: "d atrás",

    loginTitle: "Inicia sesión",
    loginEmail: "Correo electrónico",
    loginPassword: "Contraseña",
    loginBtn: "Iniciar sesión →",
    loginNoAccount: "¿No tienes cuenta?",
    loginCreateOne: "Crear una →",
    loginFailed: "Error al iniciar sesión",
    signingIn: "Iniciando sesión…",

    registerTitle: "Crear cuenta",
    registerSub: "Tu nombre de usuario será tu identidad en el pool.",
    registerUsername: "Nombre de usuario",
    registerUsernameHint: "3–20 caracteres, minúsculas/números/guion bajo",
    registerEmail: "Correo electrónico",
    registerPassword: "Contraseña",
    registerPasswordHint: "Mín. 10 caract.: mayúscula, minúscula, número y símbolo",
    hashing: "Minando",
    updated: "Actualizado",
    ago: "atrás",
    average: "promedio",
    sharesAccepted: "Shares aceptados",
    workerStatus: "Estado de mineros",
    live: "En vivo",
    faults: "Fallos",
    none: "Ninguno",
    status: "Estado",
    registerCheckEmailTitle: "Revisa tu email",
    registerCheckEmailSub: "Te enviamos un enlace de verificación a tu email. Haz clic para confirmar tu cuenta — mientras tanto puedes empezar a minar.",
    registerContinueToDashboard: "Ir al panel \u2192",
    registerResendLink: "¿No lo recibiste? Reenviar",
    registerResending: "Enviando…",
    registerResendSent: "Enviado. Debería llegar en un minuto.",
    registerResendWait: "Podrás reenviar en breve",
    registerResendFailed: "No se pudo reenviar. Inténtalo de nuevo.",
    registerCheckSpam: "Si no está en tu bandeja de entrada, revisa la carpeta de spam.",
    registerEmailUnavailableTitle: "Cuenta creada",
    registerEmailUnavailableSub: "No pudimos enviar el correo de verificación ahora. Tu cuenta funciona — contacta con soporte para verificarla.",
    btcPrice: "Precio de Bitcoin",
    backToHome: "Volver a hashrial.com",
    tenMinAverage: "Promedio de 10 minutos",
    fiveMinAverage: "Promedio de 5 minutos",
    chartCollecting: "Recopilando datos: el gráfico se completa a medida que llegan muestras.",
    degraded: "Degradado",
    degradedHint: "Degradado = por debajo del 60% del promedio de 1 hora del propio equipo.",
    perThDay: "Por TH/día",
    showIn: "Mostrar en",
    attentionCount: "{n} equipos necesitan atención",
    allRigsHealthy: "Los {n} equipos están bien",
    poolFeeNote: "Comisión del pool 2% — 1 share de cada 50.",
    payoutProgress: "Hacia el pago mínimo",
    payoutReady: "Listo para retirar",
    priceUnavailable: "Precio no disponible",
    priceDelayed: "Retrasado",
    priceDelayedHint: "La fuente de precios no está disponible: se muestra el último valor conocido.",
    currency: "Moneda",
    exchangeAtSarraf: "Cambiar en صراف →",
    rateFromSarraf: "tasa de صراف",
    rateFromSarrafHint: "Tasa tomada de صراف en lugar de la fuente pública.",
    registerBtn: "Crear cuenta →",
    registerHasAccount: "¿Ya tienes cuenta?",
    registerSignIn: "Iniciar sesión →",
    registerFailed: "Error al registrarse",
    creatingAccount: "Creando cuenta…",
    registerUsernameError: "Usuario: 3-20 caracteres, minúsculas/números/guion bajo",
    registerPasswordError: "La contraseña debe tener al menos 10 caracteres",
    registerTaken: "Usuario o correo ya registrados",
    loginForgotPassword: "¿Olvidaste tu contraseña?",
    forgotTitle: "Restablecer contraseña",
    forgotSub: "Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.",
    forgotEmail: "Email",
    forgotBtn: "Enviar enlace →",
    forgotSending: "Enviando…",
    forgotSent: "Si existe una cuenta con ese email, el enlace está en camino. Revisa tu bandeja de entrada.",
    forgotBackToLogin: "Volver a iniciar sesión",
    resetTitle: "Elige una nueva contraseña",
    resetSub: "Ingresa una nueva contraseña para tu cuenta.",
    resetPassword: "Nueva contraseña",
    resetConfirm: "Confirmar contraseña",
    resetBtn: "Restablecer contraseña →",
    resetSubmitting: "Restableciendo…",
    resetSuccess: "Contraseña restablecida. Ya puedes iniciar sesión con tu nueva contraseña.",
    resetMismatch: "Las contraseñas no coinciden",
    resetInvalidLink: "Este enlace de restablecimiento no es válido o ha expirado.",
    resetGoToLogin: "Iniciar sesión →",
    verifyTitle: "Verificando tu email…",
    verifySuccess: "¡Tu email está verificado!",
    verifySuccessSub: "Todo listo.",
    verifyFailed: "Este enlace de verificación no es válido o ha expirado.",
    verifyGoToDashboard: "Ir al panel →",
    verifyGoToLogin: "Iniciar sesión →",
  },
  pt: {
    sidebarDashboard: "Painel",
    sidebarWorkers: "Trabalhadores",
    sidebarEarnings: "Ganhos",
    sidebarConnect: "Conectar",
    sidebarNotifications: "Notificações",
    sidebarSettings: "Configurações",
    sidebarAdmin: "Admin",
    adminTitle: "Admin",
    adminSub: "Visão geral do pool, saques e conteúdo. Cada ação é reverificada no servidor.",
    adminUsers: "Usuários",
    adminVerified: "verificados",
    adminReferred: "indicados",
    adminWorkers: "Equipamentos online",
    adminOf: "de",
    adminPayoutsPending: "Saques pendentes",
    adminReferralPaid: "Recompensas pagas",
    adminCredits: "créditos",
    adminPayouts: "Solicitações de saque",
    adminNoPayouts: "Ainda não há solicitações de saque.",
    adminMarkPaid: "Marcar pago",
    adminMarkFailed: "Marcar falhou",
    adminConfirmStatus: "Alterar status do saque",
    adminTxidPrompt: "ID da transação (opcional):",
    adminArticles: "Artigos",
    adminPublished: "publicados",
    adminDrafts: "rascunho",
    adminArticle: "Artigo",
    adminWords: "Palavras",
    adminArticlesNote: "Os artigos são markdown em content/blog/. Publicar exige editar o arquivo e implantar, de propósito: o histórico do git é a auditoria, e um artigo publicável por um formulário web é um artigo publicável sem ler sua lista de verificação.",
    adminRecentUsers: "Usuários recentes",
    adminReferredBy: "Indicado por",
    adminForbiddenHint: "Esta página exige uma conta de administrador. Adicione seu ID de usuário a ADMIN_USER_IDS no wrangler.toml.",
    blogTitle: "Blog",
    blogSub: "Mineração, pools e Bitcoin — escrito para quem realmente tem equipamento rodando.",
    blogEmpty: "Nenhum artigo publicado ainda.",
    blogMinRead: "min de leitura",
    blogAll: "Todos os artigos",
    blogAlsoIn: "Também disponível em:",
    blogCtaTitle: "Comece a minerar com a Hashrial",
    blogCtaBody: "Taxa fixa de 2%, 98% para você, e um proxy feito para redes onde os grandes pools são difíceis de alcançar.",
    blogCtaAction: "Criar uma conta →",
    navBlog: "Blog",
    referralTitle: "Indique e ganhe",
    referralSub: "Ganhe 1% do que as pessoas que você trouxer minerarem — sai da nossa taxa, não da delas.",
    referralYourLink: "Seu link de indicação",
    referralCopy: "Copiar link",
    referralCopied: "Copiado",
    referralCodeIs: "Seu código:",
    referralChooseCode: "Escolha o seu próprio código",
    referralCodePlaceholder: "ex. SINA",
    referralClaimCode: "Reservar",
    referralSaving: "Salvando…",
    referralCodeHint: "4–16 letras e números. Escolha algo fácil de falar em voz alta.",
    referralCodeKeepsWorking: "Os links que você já compartilhou continuam funcionando — cada código reservado é seu para sempre.",
    referralCodeSaved: "Código atualizado",
    referralCodeTaken: "Esse código já está em uso",
    referralCodeReserved: "Esse código é reservado",
    referralCodeInvalid: "Use apenas 4–16 letras e números",
    referralCodeFailed: "Não foi possível salvar. Tente novamente.",
    referralEarned: "Ganhos por indicação",
    referralLast30: "Últimos 30 dias",
    referralRate: "Sua parte",
    referralPeople: "Pessoas que você trouxe",
    referralActiveNote: "Ativo significa que a conta verificou o e-mail e pode minerar. Cadastros não verificados são contados à parte.",
    referralHowTitle: "Como funciona",
    referralHowBody: "A Hashrial cobra uma taxa de 2% sobre o que um minerador ganha. Quando alguém se cadastra pelo seu link, você recebe metade dessa taxa — 1% de tudo o que essa pessoa minerar, enquanto ela minerar. Sai da nossa parte, então quem você convidou continua ficando com os mesmos 98%. As recompensas são creditadas diariamente.",
    referralYourPeople: "Suas indicações",
    referralNobodyYet: "Ninguém se cadastrou pelo seu link ainda. Compartilhe e suas recompensas aparecerão aqui.",
    referralUser: "Usuário",
    referralJoined: "Entrou",
    referralActive: "Ativo",
    referralPendingVerify: "Não verificado",
    sidebarReferral: "Indique e ganhe",
    headerWelcome: "Bem-vindo de volta",
    headerLive: "Ao vivo",
    signOut: "Sair",
    darkMode: "Modo escuro",
    lightMode: "Modo claro",
    language: "Idioma",
    loading: "Carregando…",
    loading2: "Carregando painel…",
    loadingWorkers: "Carregando trabalhadores…",
    noData: "Sem dados",
    noDataWorker: "Sem dados ainda para este trabalhador.",
    errorPrefix: "⚠",

    dashboardTitle: "Painel",
    dashboardSub: "Estatísticas ao vivo · atualiza a cada 60s",
    hashrate10m: "Hashrate (10m)",
    hashrate5m: "Hashrate (5m)",
    activeWorkers: "Trabalhadores ativos",
    balance: "Saldo",
    last24h: "Últimas 24h",
    totalEarned: "Total ganho",
    acceptance: "Aceitação",
    hashrateHistory: "Histórico de hashrate",
    noHashrateData: "Sem dados. Conecte seus mineradores.",
    viewAll: "Ver tudo →",

    workersTitle: "Trabalhadores",
    workersSub: "Todos os mineradores conectados à sua conta",
    total: "Total",
    online: "online",
    offline: "offline",
    all: "todos",
    noWorkers: "Nenhum trabalhador ainda.",
    connectFirst: "Conecte seu primeiro minerador →",
    colWorker: "Trabalhador",
    colStatus: "Status",
    col10m: "10m",
    col5m: "5m",
    col1h: "1h",
    col24h: "24h",
    colAccepted: "Aceito",
    colStale: "Obsoleto",
    colLastSeen: "Última vez",

    workerDetail: "Detalhes do trabalhador",
    backToWorkers: "← Voltar para trabalhadores",
    workerNotFound: "Trabalhador não encontrado.",
    hashrateChart: "Hashrate (24h)",
    shares: "Ações",
    accepted: "Aceito",
    stale: "Obsoleto",

    earningsTitle: "Ganhos",
    earningsSub: "Histórico de ganhos e pagamentos",
    date: "Data",
    amount: "Valor",
    pending: "Pendente",
    processing: "Processando",
    completed: "Concluído",
    failed: "Falhou",
    address: "Endereço",
    txid: "Transação",
    records: "registros",
    balance2: "Saldo",
    earn24h: "Ganhos 24h",
    earnTotal: "Total ganho",
    paidOut: "Pago",
    settleDate: "Data de liquidação",
    loadingEarnings: "Carregando ganhos…",
    noEarnings: "Sem dados de ganhos.",
    requestPayout: "Solicitar Pagamento",
    payoutHistory: "Histórico de Pagamentos",
    earningsHistory: "Histórico de Ganhos",
    submitting: "Enviando",
    payoutMinNote: "Certifique-se de ter definido um endereço de pagamento nas Configurações.",
    accountInfo: "Conta",
    memberSince: "Membro desde",
    settingsUserId: "ID de usuário",
    page: "Pág.",
    of: "de",
    prev: "← Anterior",
    next: "Próximo →",

    connectTitle: "Conecte seus mineradores",
    connectIntro: "Configure seu ASIC ou software com estas opções.",
    stratumHost: "Host Stratum",
    stratumPort: "Porta",
    workerLabel: "Nome do trabalhador",
    workerExample: "Substitua WORKER_NAME por qualquer rótulo",
    poolFee: "Taxa do pool de 2%.",
    connectSteps: "Instruções",
    step1: "Acesse o painel do minerador",
    step2: "Defina a URL para",
    step3: "Defina o Worker para",
    step4: "Salve e reinicie o minerador",
    note: "Nota",
    noteText: "Seu minerador aparecerá em Workers em 60 segundos.",
    whatTitle: "O que esperar",
    what1: "Seu hashrate aparece ao vivo no Painel",
    what2: "Cada trabalhador é rastreado separadamente",
    what3: "Ganhos atualizados a cada 60s",
    what4: "Pagamentos disponíveis acima de 0.001 BTC",

    settingsTitle: "Configurações",
    settingsSub: "Gerencie suas preferências",
    bitcoinAddress: "Endereço Bitcoin",
    bitcoinAddressLabel: "Seu endereço BTC para pagamentos",
    bitcoinAddressHint: "Insira seu endereço de carteira Bitcoin.",
    save: "Salvar",
    saved: "Salvo!",
    notifications: "Notificações",
    notifyOffline: "Alertas de trabalhador offline",
    notifyOfflineDesc: "Notificar quando um trabalhador desconectar",
    notifyHashrate: "Alertas de queda de hashrate",
    notifyHashrateDesc: "Notificar quando o hashrate cair significativamente",
    notifyThreshold: "Limiar de queda (%)",
    notifyThresholdDesc: "Porcentagem mínima de queda para alertar",
    saving: "Salvando…",

    notifTitle: "Notificações",
    notifSub: "Alertas sobre sua atividade de mineração",
    noNotifs: "Nenhuma notificação.",
    markAllRead: "Marcar tudo como lido",
    unread: "não lidas",
    notifWorkerOffline: "Trabalhador offline",
    notifWorkerOnline: "Trabalhador online",
    notifHashrateDrop: "Queda de hashrate",
    justNow: "agora mesmo",
    minsAgo: "min atrás",
    hoursAgo: "h atrás",
    daysAgo: "d atrás",

    loginTitle: "Entre na sua conta",
    loginEmail: "Email",
    loginPassword: "Senha",
    loginBtn: "Entrar →",
    loginNoAccount: "Não tem conta?",
    loginCreateOne: "Criar →",
    loginFailed: "Falha ao entrar",
    signingIn: "Entrando…",

    registerTitle: "Criar conta",
    registerSub: "Seu nome de usuário será sua identidade no pool.",
    registerUsername: "Nome de usuário",
    registerUsernameHint: "3–20 caracteres, minúsculas/números/sublinhado",
    registerEmail: "Email",
    registerPassword: "Senha",
    registerPasswordHint: "Mín. 10 caract.: maiúscula, minúscula, número e símbolo",
    hashing: "Minerando",
    updated: "Atualizado",
    ago: "atrás",
    average: "média",
    sharesAccepted: "Shares aceitos",
    workerStatus: "Estado dos mineradores",
    live: "Ao vivo",
    faults: "Falhas",
    none: "Nenhum",
    status: "Estado",
    registerCheckEmailTitle: "Verifique seu email",
    registerCheckEmailSub: "Enviamos um link de verificação para o seu email. Clique nele para confirmar sua conta — enquanto isso você já pode começar a minerar.",
    registerContinueToDashboard: "Ir ao painel \u2192",
    registerResendLink: "Não recebeu? Reenviar",
    registerResending: "Enviando…",
    registerResendSent: "Enviado. Deve chegar em um minuto.",
    registerResendWait: "Você poderá reenviar em breve",
    registerResendFailed: "Não foi possível reenviar. Tente novamente.",
    registerCheckSpam: "Se não estiver na caixa de entrada, verifique a pasta de spam.",
    registerEmailUnavailableTitle: "Conta criada",
    registerEmailUnavailableSub: "Não conseguimos enviar o e-mail de verificação agora. Sua conta funciona — contate o suporte para verificá-la.",
    btcPrice: "Preço do Bitcoin",
    backToHome: "Voltar para hashrial.com",
    tenMinAverage: "Média de 10 minutos",
    fiveMinAverage: "Média de 5 minutos",
    chartCollecting: "Coletando dados — o gráfico se preenche conforme as amostras chegam.",
    degraded: "Degradado",
    degradedHint: "Degradado = abaixo de 60% da média de 1 hora do próprio equipamento.",
    perThDay: "Por TH/dia",
    showIn: "Mostrar em",
    attentionCount: "{n} equipamentos precisam de atenção",
    allRigsHealthy: "Todos os {n} equipamentos estão bem",
    poolFeeNote: "Taxa do pool 2% — 1 share a cada 50.",
    payoutProgress: "Até o saque mínimo",
    payoutReady: "Pronto para sacar",
    priceUnavailable: "Preço indisponível",
    priceDelayed: "Atrasado",
    priceDelayedHint: "A fonte de preços está indisponível — exibindo o último valor conhecido.",
    currency: "Moeda",
    exchangeAtSarraf: "Trocar na صراف →",
    rateFromSarraf: "taxa da صراف",
    rateFromSarrafHint: "Taxa obtida da صراف em vez da fonte pública.",
    registerBtn: "Criar conta →",
    registerHasAccount: "Já tem conta?",
    registerSignIn: "Entrar →",
    registerFailed: "Falha ao registrar",
    creatingAccount: "Criando conta…",
    registerUsernameError: "Usuário: 3-20 caracteres, minúsculas/números/sublinhado",
    registerPasswordError: "Senha deve ter pelo menos 10 caracteres",
    registerTaken: "Usuário ou email já registrados",
    loginForgotPassword: "Esqueceu a senha?",
    forgotTitle: "Redefinir senha",
    forgotSub: "Digite seu email e enviaremos um link para redefinir sua senha.",
    forgotEmail: "Email",
    forgotBtn: "Enviar link →",
    forgotSending: "Enviando…",
    forgotSent: "Se existir uma conta com esse email, o link está a caminho. Verifique sua caixa de entrada.",
    forgotBackToLogin: "Voltar ao login",
    resetTitle: "Escolha uma nova senha",
    resetSub: "Digite uma nova senha para sua conta.",
    resetPassword: "Nova senha",
    resetConfirm: "Confirmar senha",
    resetBtn: "Redefinir senha →",
    resetSubmitting: "Redefinindo…",
    resetSuccess: "Senha redefinida. Agora você pode entrar com sua nova senha.",
    resetMismatch: "As senhas não coincidem",
    resetInvalidLink: "Este link de redefinição é inválido ou expirou.",
    resetGoToLogin: "Entrar →",
    verifyTitle: "Verificando seu email…",
    verifySuccess: "Seu email foi verificado!",
    verifySuccessSub: "Tudo pronto.",
    verifyFailed: "Este link de verificação é inválido ou expirou.",
    verifyGoToDashboard: "Ir ao painel →",
    verifyGoToLogin: "Entrar →",
  },
};

const LangContext = createContext();

// Which language a visitor sees first. An explicit ?lang= link wins; then the
// visitor's own earlier choice; then where they came from and what their
// browser asks for. Persian readers arrive from Persian articles (Virgool links
// carry utm_source=virgool) and used to land on the English page until they
// found the switcher. Crawlers send an English Accept-Language, so what Google
// indexes does not change.
function initialLang() {
  const codes = LANGS.map(l => l.code);
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = (q.get("lang") || "").toLowerCase();
    if (codes.includes(fromUrl)) return fromUrl;
    const saved = localStorage.getItem("hashrial_lang");
    if (codes.includes(saved)) return saved;
    if ((q.get("utm_source") || "").toLowerCase() === "virgool") return "fa";
    const browser = (navigator.languages && navigator.languages[0]) || navigator.language || "";
    const base = browser.toLowerCase().split("-")[0];
    if (codes.includes(base)) return base;
  } catch (e) {
    // localStorage can throw in private modes; English is the safe default.
  }
  return "en";
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(initialLang);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.dir = (LANGS.find(l => l.code === lang) || {}).dir || "ltr";
    localStorage.setItem("hashrial_lang", lang);
  }, [lang]);

  function t(key) {
    const langData = T[lang] || T.en;
    return langData[key] || T.en[key] || key;
  }

  return (
    <LangContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
